/**
 * Wide Event Logger
 *
 * Implements the "wide events" / "canonical log lines" pattern:
 * Instead of scattered console.logs, build ONE comprehensive event
 * per request/action with ALL relevant context, emit at the end.
 *
 * Outputs to:
 * 1. Axiom (observability platform - free tier: 500GB/month)
 * 2. MongoDB (as a new "wide-event" log type for comparison)
 *
 * Usage:
 *   const event = createWideEvent('page-load');
 *   event.set('user', username);
 *   event.set('location', 'PHX');
 *   event.set('projectCount', 42);
 *   event.addTiming('dataLoad', 1234);
 *   await event.send();
 *
 * @see https://www.youtube.com/watch?v=... (Theo's video on wide events)
 */

// Axiom configuration
const AXIOM_DATASET = process.env.REACT_APP_AXIOM_DATASET || 'scheduler-events';
const AXIOM_TOKEN = process.env.REACT_APP_AXIOM_TOKEN;
const AXIOM_API_URL = 'https://cloud.axiom.co/api/v1/datasets';

/**
 * Creates a new wide event that accumulates context over time
 * and sends to both Axiom and MongoDB when complete.
 *
 * @param {string} eventName - The name/type of this event (e.g., 'page-load', 'drag-drop', 'mutation')
 * @param {object} options - Configuration options
 * @param {function} options.mongoLogger - Function to send to MongoDB (typically createLogEntry.mutate)
 * @param {string} options.mongoDatabase - MongoDB database name
 * @returns {WideEvent} - Event builder with set(), addTiming(), addError(), send() methods
 */
export function createWideEvent(eventName, options = {}) {
  const startTime = performance.now();
  const startTimestamp = new Date().toISOString();

  // The wide event data structure - starts with basics
  const eventData = {
    // Core identification
    _eventName: eventName,
    _timestamp: startTimestamp,
    _source: 'scheduler-app',
    _env: process.env.REACT_APP_ENV || 'development',
    _appVersion: null, // Set via set() when available

    // Will be populated via set()
    user: null,
    userEmail: null,
    location: null,
    companyCode: null,
    conversion: null, // tanstackOnly, realmOnly, both

    // Timings (in ms)
    _timings: {},

    // Counts/metrics
    _counts: {},

    // Errors encountered
    _errors: [],

    // Custom fields added via set()
    _custom: {},
  };

  const { mongoLogger, mongoDatabase } = options;

  return {
    /**
     * Set a field on the event. Can be called multiple times.
     * @param {string} key - Field name
     * @param {any} value - Field value
     */
    set(key, value) {
      // Known top-level fields go directly on eventData
      const topLevelFields = ['user', 'userEmail', 'location', 'companyCode', 'conversion', '_appVersion'];
      if (topLevelFields.includes(key)) {
        eventData[key] = value;
      } else {
        eventData._custom[key] = value;
      }
      return this; // Allow chaining
    },

    /**
     * Add a timing measurement
     * @param {string} name - Name of the operation (e.g., 'dataLoad', 'apiCall')
     * @param {number} durationMs - Duration in milliseconds
     */
    addTiming(name, durationMs) {
      eventData._timings[name] = durationMs;
      return this;
    },

    /**
     * Add a count/metric
     * @param {string} name - Name of the metric (e.g., 'projectCount', 'candidateCount')
     * @param {number} count - The count value
     */
    addCount(name, count) {
      eventData._counts[name] = count;
      return this;
    },

    /**
     * Record an error that occurred during this event
     * @param {string} context - Where the error occurred
     * @param {Error|string} error - The error object or message
     */
    addError(context, error) {
      eventData._errors.push({
        context,
        message: error?.message || String(error),
        stack: error?.stack?.substring(0, 500), // Truncate stack traces
        timestamp: new Date().toISOString(),
      });
      return this;
    },

    /**
     * Get current event data (for debugging)
     */
    getData() {
      return { ...eventData };
    },

    /**
     * Send the event to both Axiom and MongoDB
     * Call this when the event is complete (end of page load, after mutation, etc.)
     */
    async send() {
      // Calculate total duration
      const endTime = performance.now();
      const totalDurationMs = Math.round(endTime - startTime);

      // Flatten the event for sending
      const flatEvent = {
        eventName: eventData._eventName,
        timestamp: eventData._timestamp,
        source: eventData._source,
        env: eventData._env,
        appVersion: eventData._appVersion,

        // User context
        user: eventData.user,
        userEmail: eventData.userEmail,
        location: eventData.location,
        companyCode: eventData.companyCode,
        conversion: eventData.conversion,

        // Performance
        totalDurationMs,
        timings: eventData._timings,

        // Metrics
        counts: eventData._counts,

        // Errors (if any)
        errorCount: eventData._errors.length,
        errors: eventData._errors.length > 0 ? eventData._errors : undefined,

        // Custom fields (spread at top level for easier querying)
        ...eventData._custom,
      };

      // Send to both destinations in parallel
      const promises = [];

      // 1. Send to Axiom (if token configured)
      if (AXIOM_TOKEN) {
        promises.push(sendToAxiom(flatEvent));
      } else {
        console.log(
          '%c[WideEvent] Axiom not configured (no REACT_APP_AXIOM_TOKEN)',
          'color: #FFA500;'
        );
      }

      // 2. Send to MongoDB (if logger provided)
      if (mongoLogger) {
        promises.push(sendToMongoDB(flatEvent, mongoLogger, mongoDatabase));
      }

      // Wait for all sends to complete (don't block on failures)
      const results = await Promise.allSettled(promises);

      // Log any failures
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(
            `%c[WideEvent] Failed to send to ${index === 0 ? 'Axiom' : 'MongoDB'}:`,
            'color: #FF6B6B;',
            result.reason
          );
        }
      });

      // Console log for visibility
      console.log(
        '%c[WideEvent] Sent to Axiom',
        'color: #9370DB;',
        flatEvent
      );

      return flatEvent;
    },
  };
}

/**
 * Send event to Axiom
 */
async function sendToAxiom(event) {
  const url = `${AXIOM_API_URL}/${AXIOM_DATASET}/ingest`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${AXIOM_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify([event]), // Axiom expects an array of events
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '%c[WideEvent] Axiom request failed',
        'color: #FF6B6B;',
        { status: response.status, statusText: response.statusText, errorText }
      );
      throw new Error(`Axiom ingest failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error(
      '%c[WideEvent] Axiom request error',
      'color: #FF6B6B;',
      error
    );
    throw error;
  }
}

/**
 * Send event to MongoDB as a "wide-event" log type
 */
async function sendToMongoDB(event, mongoLogger, mongoDatabase) {
  // Transform to match existing MongoDB log structure
  const mongoLogEntry = {
    type: 'wide-event',
    eventName: event.eventName,
    user: event.user,
    when: event.timestamp,
    whenDate: event.timestamp.split('T')[0], // YYYY-MM-DD

    // Include all the wide event data
    wideEventData: event,
  };

  // Use the provided logger (createLogEntry.mutate)
  return mongoLogger(mongoLogEntry);
}

/**
 * Helper to create timestamp in consistent format
 */
export function getTimestamp() {
  const now = new Date();
  return {
    when: now.toISOString(),
    whenDate: now.toISOString().split('T')[0],
  };
}

/**
 * Singleton event builder for session-wide events
 * Useful for tracking data across the entire user session
 */
let sessionEvent = null;

export function getSessionEvent() {
  if (!sessionEvent) {
    sessionEvent = createWideEvent('session');
  }
  return sessionEvent;
}

export function resetSessionEvent() {
  sessionEvent = null;
}

// (#79) Named the default export so it shows up cleanly in stack traces and
// satisfies eslint's `import/no-anonymous-default-export` rule.
const wideEventLogger = {
  createWideEvent,
  getSessionEvent,
  resetSessionEvent,
  getTimestamp,
};

export default wideEventLogger;
